import type { CoverageData, EndorsementRateData } from './types';

const normalize = (s: string): string =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9%/]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const buildFormVariants = (
    form: string,
    coverages: CoverageData[]
): Set<string> => {
    const formNorm = normalize(form);
    const variants = new Set<string>();
    variants.add(form.toLowerCase());
    variants.add(formNorm);

    const direct = coverages.find(
        c =>
            normalize(c.coverage) === formNorm ||
            c.coverage.toLowerCase() === form.toLowerCase()
    );
    if (direct) {
        variants.add(direct.coverage.toLowerCase());
        variants.add(normalize(direct.coverage));
        direct.coverage_alias?.forEach(alias => {
            variants.add(alias.toLowerCase());
            variants.add(normalize(alias));
        });
    }

    coverages.forEach(c => {
        const covNorm = normalize(c.coverage);
        const matchesByName =
            variants.has(covNorm) || variants.has(c.coverage.toLowerCase());
        const matchesByAlias = c.coverage_alias?.some(
            alias =>
                variants.has(alias.toLowerCase()) ||
                variants.has(normalize(alias))
        );
        if (matchesByName || matchesByAlias) {
            variants.add(c.coverage.toLowerCase());
            variants.add(covNorm);
            c.coverage_alias?.forEach(alias => {
                variants.add(alias.toLowerCase());
                variants.add(normalize(alias));
            });
        }
    });

    return variants;
};

const formMatches = (
    storedForm: string,
    requestedForm: string,
    variants: Set<string>
): boolean => {
    const stored = storedForm.toLowerCase();
    if (stored === 'all') return true;
    const storedNorm = normalize(storedForm);
    if (variants.has(stored) || variants.has(storedNorm)) return true;
    const requestedNorm = normalize(requestedForm);
    return (
        requestedNorm.startsWith(storedNorm) ||
        storedNorm.startsWith(requestedNorm)
    );
};

const countyMatches = (
    storedCounty: string,
    requestedCounty: string
): boolean => {
    if (storedCounty === '') return true;
    const stored = storedCounty.toLowerCase();
    if (stored === 'all') return true;
    return stored === requestedCounty.toLowerCase();
};

const isCountyWildcard = (storedCounty: string): boolean =>
    storedCounty === '' || storedCounty.toLowerCase() === 'all';

export interface FindEndorsementRateArgs {
    carrier: string;
    crop: string;
    county: string;
    form: string;
    endorsement: string;
    coverages: CoverageData[];
}

/**
 * Build the dictionary key used by the saved quote `endorsementRateLookup`.
 *
 * The shape includes `crop` and `county` so a single quote with multiple
 * locations stores one rate per (carrier, product, endorsement, crop, county)
 * combination. Earlier quotes were saved without crop/county in the key —
 * see `readSavedEndorsementRate` for the legacy fallback.
 */
export const buildEndorsementLookupKey = (
    carrier: string,
    product: string,
    endorsement: string,
    crop: string,
    county: string
): string =>
    `${carrier}|${product}|${endorsement}|${crop.toLowerCase()}|${county.toLowerCase()}`;

/**
 * Read a rate from a saved quote's endorsement lookup, accepting both the
 * current key (with crop+county) and the legacy key shape that pre-dated the
 * multi-crop fix. Without the fallback, every quote saved before the fix
 * would render blank endorsement cells.
 */
export const readSavedEndorsementRate = (
    lookup: Record<string, number> | undefined,
    carrier: string,
    product: string,
    endorsement: string,
    crop: string,
    county: string
): number | null => {
    if (!lookup) return null;
    const currentKey = buildEndorsementLookupKey(
        carrier,
        product,
        endorsement,
        crop,
        county
    );
    if (lookup[currentKey] != null) return lookup[currentKey];
    const legacyKey = `${carrier}|${product}|${endorsement}`;
    if (lookup[legacyKey] != null) return lookup[legacyKey];
    return null;
};

/**
 * Locate a single endorsement rate that matches the requested
 * carrier / crop / county / form / endorsement combination.
 *
 * A specific-county row beats a wildcard ("all" or "") county row when both
 * are present, so the caller can hand in the full unfiltered rate set.
 */
export function findEndorsementRate(
    rates: EndorsementRateData[],
    args: FindEndorsementRateArgs
): number | null {
    const { carrier, crop, county, form, endorsement, coverages } = args;
    const variants = buildFormVariants(form, coverages);
    const cropLower = crop.toLowerCase();

    let wildcard: EndorsementRateData | undefined;
    for (const r of rates) {
        if (r.carrier !== carrier) continue;
        if (r.endorsement !== endorsement) continue;
        if (r.crop.toLowerCase() !== cropLower) continue;
        if (!countyMatches(r.county, county)) continue;
        if (!formMatches(r.form, form, variants)) continue;

        if (isCountyWildcard(r.county)) {
            if (!wildcard) wildcard = r;
            continue;
        }
        return r.rate;
    }
    return wildcard ? wildcard.rate : null;
}
